import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface TimeZone {
  id: number
  name: string
  abbreviation: string | null
  utc_offset: number
  status: number
  usage_count: number
}

interface Props {
  time_zones: TimeZone[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/time_zones" },
  { title: "Time Zones", href: "/admin/time_zones" },
]

const HOME_TZ_OFFSET = -5 // EST

const hours = Array.from({ length: 12 }, (_, i) => i + 1)
const minutesSeconds = Array.from({ length: 60 }, (_, i) => i)

function padTwo(n: number): string {
  return n < 10 ? `0${n}` : `${n}`
}

export default function Index({ time_zones }: Props) {
  const [startH, setStartH] = useState(12)
  const [startM, setStartM] = useState(0)
  const [startS, setStartS] = useState(0)
  const [startT, setStartT] = useState("AM")
  const [startZ, setStartZ] = useState(0)
  const [conversionResult, setConversionResult] = useState<string | null>(null)

  const activeZones = time_zones
    .filter((tz) => tz.status === 0)
    .sort((a, b) => b.utc_offset - a.utc_offset || a.name.localeCompare(b.name))

  const handleDelete = (id: number, usageCount: number) => {
    const message =
      usageCount > 0
        ? "This time zone is in use. It will be deactivated instead of deleted. Continue?"
        : "Are you sure you want to delete this time zone?"

    if (confirm(message)) {
      router.delete(`/admin/time_zones/${id}`)
    }
  }

  const handleTest = (e: React.FormEvent) => {
    e.preventDefault()

    const today = new Date()
    const dateStr = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`

    // Build the original time string
    const origTime = `${dateStr} ${startH}:${padTwo(startM)}:${padTwo(startS)} ${startT}`

    if (startZ === 0) {
      const resultTime = `${dateStr} ${startH}:${padTwo(startM)} ${startT}`
      setConversionResult(`${origTime}  converted to = ${resultTime} EST`)
      return
    }

    const zone = activeZones.find((tz) => tz.id === startZ)
    if (!zone) return

    const tzOffset = zone.utc_offset
    const zoneName = zone.abbreviation ?? zone.name

    // Convert 12-hour to 24-hour for calculation
    let hour24 = startH
    if (startT === "AM" && startH === 12) hour24 = 0
    if (startT === "PM" && startH !== 12) hour24 += 12

    // Apply offset: convertToTimeZone(date, fromOffset, toOffset)
    // Legacy: fromOffset - toOffset hours added
    // fromOffset = HOME_TZ_OFFSET (-5), toOffset = tzOffset
    const shiftHours = HOME_TZ_OFFSET - tzOffset
    let resultHour = hour24 + shiftHours
    let resultMin = startM

    // Handle fractional offsets (e.g., India +5.5)
    const fractionalHours = shiftHours % 1
    if (fractionalHours !== 0) {
      resultHour = Math.floor(hour24 + shiftHours)
      resultMin = startM + Math.round(fractionalHours * 60)
      if (resultMin >= 60) {
        resultMin -= 60
        resultHour += 1
      } else if (resultMin < 0) {
        resultMin += 60
        resultHour -= 1
      }
    }

    // Track day offset for midnight crossing
    let dayShift = 0
    if (resultHour >= 24) {
      dayShift = Math.floor(resultHour / 24)
      resultHour = resultHour % 24
    } else if (resultHour < 0) {
      dayShift = Math.ceil(resultHour / 24) - 1
      resultHour = ((resultHour % 24) + 24) % 24
    }

    // Build result date
    const resultDate = new Date(today)
    resultDate.setDate(resultDate.getDate() + dayShift)
    const resultDateStr = `${resultDate.getMonth() + 1}/${resultDate.getDate()}/${resultDate.getFullYear()}`

    // Convert back to 12-hour
    const resultT = resultHour >= 12 ? "PM" : "AM"
    let resultH12 = resultHour % 12
    if (resultH12 === 0) resultH12 = 12

    const resultTime = `${resultDateStr} ${resultH12}:${padTwo(resultMin)} ${resultT}`

    setConversionResult(
      `${origTime} ${zoneName} converted to = ${resultTime} EST`,
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Time Zones" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Time Zones</h1>
          <Link
            href="/admin/time_zones/new"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Time Zone
          </Link>
        </div>

        {time_zones.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  ID
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  In Use
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {time_zones.map((tz) => (
                <tr key={tz.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {tz.id}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {tz.name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {tz.usage_count > 0 ? tz.usage_count : ""}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/admin/time_zones/${tz.id}/edit`}
                      className="text-hpi-active-blue mr-2 hover:underline"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(tz.id, tz.usage_count)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No time zones found.</p>
        )}

        <div className="mt-8 max-w-md">
          <h2 className="mb-4 text-xl font-semibold">Test Conversion</h2>
          <form onSubmit={handleTest}>
            <div className="mb-4">
              <label className="text-sm font-medium">Time</label>
              <div className="mt-1 flex items-center gap-1">
                <select
                  value={startH}
                  onChange={(e) => setStartH(Number(e.target.value))}
                  className="border-hpi-light-gray rounded border px-2 py-1"
                >
                  {hours.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={startM}
                  onChange={(e) => setStartM(Number(e.target.value))}
                  className="border-hpi-light-gray rounded border px-2 py-1"
                >
                  {minutesSeconds.map((m) => (
                    <option key={m} value={m}>
                      {padTwo(m)}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={startS}
                  onChange={(e) => setStartS(Number(e.target.value))}
                  className="border-hpi-light-gray rounded border px-2 py-1"
                >
                  {minutesSeconds.map((s) => (
                    <option key={s} value={s}>
                      {padTwo(s)}
                    </option>
                  ))}
                </select>
                <select
                  value={startT}
                  onChange={(e) => setStartT(e.target.value)}
                  className="border-hpi-light-gray rounded border px-2 py-1"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
                <select
                  value={startZ}
                  onChange={(e) => setStartZ(Number(e.target.value))}
                  className="border-hpi-light-gray rounded border px-2 py-1"
                >
                  <option value={0}>-- Select Zone --</option>
                  {activeZones.map((tz) => (
                    <option key={tz.id} value={tz.id}>
                      {tz.abbreviation ?? tz.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="submit"
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Test
            </button>
          </form>

          {conversionResult && (
            <div className="bg-hpi-pale-blue mt-4 rounded p-3 text-sm">
              {conversionResult}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
